import * as React from 'react';
import cls from 'classnames';

import logoUrl from './assets/logo.png';
import whiteLogoUrl from './assets/white-logo.png';
import { LocationsListing } from '../locations-listsing';
import AuthModal from '../modal-signin';
import { Typeahead } from '../typeahead';
import HeaderRightLinksCouple from './HeaderRightLinksCouple';
import HeaderRightLinksVendor from './HeaderRightLinksVendor';
import HeaderRightLinksUnauth from './HeaderRightLinksUnauth';
import MwebHeaderMenuVendor from './MwebHeaderMenuVendor';
import MwebHeaderMenuUnauth from './MwebHeaderMenuUnauth';
import MwebHeaderMenuCouple from './MwebHeaderMenuCouple';

import VenuesShoppingCart from './VenuesShoppingCart';
import { HeaderContext, HeaderContextType, HeaderContextMenus } from './HeaderContext';
import { WSAppContext, WSAppContextType, UserInfo } from '../../providers/ws-app';

import styles from './Header.module.scss';

export interface Props {
    // Common props
    unreadMessagesCount: number;
    unreadReviewsCount: number;
    collapsed: boolean;
    isWhiteStyle: boolean;

    // Shopping Cart props
    showShoppingCart: boolean;
    showShoppingCartExperience?: boolean;

    // Desktop only props
    readyForSplash: boolean;
    overlayStyle: boolean;
    locationsMenuTrackingSource?: string;
    hideTypeahead: boolean;

    onCreateNewAccountSuccess?: () => void;
    onFacebookConnectSuccess?: (isSignup: boolean) => void;
}

interface HeaderState {
    scrolledToTop: boolean;
}

const { HAMBURGER, SUBHEADER_LINKS, LOCATIONS, ACCOUNT } = HeaderContextMenus;

export class Header extends React.Component<Props, HeaderState> {
    static contextType: React.Context<HeaderContextType> = HeaderContext;
    // @ts-ignore
    context!: React.ContextType<typeof HeaderContext>;

    static defaultProps: Partial<Props> = {
        collapsed: false,
    };

    state: HeaderState = {
        scrolledToTop: false,
    };

    typeaheadLocationsContainer: HTMLDivElement;
    containerRef: HTMLDivElement | undefined;
    handleClickOutsideHamburgerMenuAndBrowseVenues: (e: Event) => void;
    handleClickOutsideTypeaheadContainer: (e: Event) => void;

    constructor(props: Props) {
        super(props);

        this.toggleContextMenu = this.toggleContextMenu.bind(this);
    }

    componentDidMount() {
        const findDOMNode = require('react-dom').findDOMNode;
        const headerContainer = this.containerRef;

        // We want to hide the hamburger menu if the user clicks outside
        this.handleClickOutsideHamburgerMenuAndBrowseVenues = (e: Event) => {
            if (headerContainer && !headerContainer.contains(e.target as Node)) {
                this.context.closeContextMenu(HAMBURGER);
                this.context.closeContextMenu(SUBHEADER_LINKS);
            }
        };
        document.addEventListener('click', this.handleClickOutsideHamburgerMenuAndBrowseVenues, false);

        // Hide the locations dropdown if we click outside of it
        this.handleClickOutsideTypeaheadContainer = (e: Event) => {
            const typeaheadNode = findDOMNode(this.typeaheadLocationsContainer);
            if (!!typeaheadNode && !typeaheadNode.contains(e.target as Node) && this.context.isContextMenuOpen(LOCATIONS)) {
                this.context.closeContextMenu(LOCATIONS);
            }
        };
        document.addEventListener('click', this.handleClickOutsideTypeaheadContainer, false);

        // determine initial state for page scroll offset and toggle header styles for overlayStyle
        if (this.props.overlayStyle) {
            const scrollOffset = window.scrollY;
            this.setState({ scrolledToTop: scrollOffset === 0 });
            window.addEventListener('scroll', () => {
                const scrollOffset = window.scrollY;
                this.setState({ scrolledToTop: scrollOffset === 0 });
            });
        }

        this.context.loadHeaderData();
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.handleClickOutsideHamburgerMenuAndBrowseVenues);
        document.removeEventListener('click', this.handleClickOutsideTypeaheadContainer);
    }

    render() {
        const { collapsed, isWhiteStyle, readyForSplash, overlayStyle, showShoppingCart, showShoppingCartExperience } = this.props;

        return (
            <WSAppContext.Consumer>
                {(appContext: WSAppContextType) => {
                    const { userIsAuthenticated, user } = appContext;
                    return (
                        <div
                            className={cls(
                                styles.className,
                                { [styles.collapsed]: collapsed },
                                { [styles.whiteStyle]: isWhiteStyle },
                                { [styles.readyForSplash]: readyForSplash },
                                {
                                    [styles.overlayStyle]: overlayStyle,
                                    [styles.overlayStyleTop]: overlayStyle && this.state.scrolledToTop,
                                    [styles.whiteStyle]: overlayStyle && this.state.scrolledToTop,
                                    [styles.readyForSplash]: overlayStyle && this.state.scrolledToTop,
                                }
                            )}
                            ref={(e: HTMLDivElement) => (this.containerRef = e)}
                            data-testid='Header--container'
                        >
                            {/* Left side links and search bar */}
                            {this.renderLeftSideLinks()}

                            {showShoppingCart && <VenuesShoppingCart withExperience={showShoppingCartExperience} />}

                            {/* Desktop right side links and menus */}
                            {this.renderRightLinks(userIsAuthenticated, user)}

                            {/* Mweb hamburger menu */}
                            {this.renderMwebMenu(userIsAuthenticated, user)}

                            {/* Modal components for signin/signup */}
                            {this.renderAuthModals()}
                        </div>
                    );
                }}
            </WSAppContext.Consumer>
        );
    }

    private renderLeftSideLinks() {
        const { isContextMenuOpen, openContextMenu, closeContextMenu } = this.context;

        return (
            <>
                <div className={styles.logo}>
                    <a
                        href='#'
                        className={styles.hamburger}
                        onClick={(e) => {
                            e.preventDefault();
                            this.toggleContextMenu(HAMBURGER);
                            this.context.headerItemClicked('Menu');
                        }}
                    >
                        ☰
                    </a>
                    <a
                        href='/'
                        className={styles.logoLink}
                        onClick={() => this.context.headerItemClicked('Logo')}
                        data-testid='Header--logo'
                    >
                        {/* <img className={styles.whiteLogo} src={whiteLogoUrl?.src || whiteLogoUrl} /> */}
                        <img
                            className={styles.regularLogo}
                            src={`data:image/png;base64,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`}
                            alt='Wedding Spot logo'
                        />
                    </a>
                </div>

                {/* Search bar and typeahead */}
                {!this.props.hideTypeahead && (
                    <div
                        className={styles.typeahead}
                        ref={(el: HTMLDivElement) => {
                            this.typeaheadLocationsContainer = el;
                        }}
                    >
                        <Typeahead
                            isSearchIconShown={true}
                            onInputFocus={(value: string) => {
                                if (!value) {
                                    openContextMenu(LOCATIONS);
                                }
                            }}
                            onInputChange={(value: string) => {
                                if (!!value) {
                                    closeContextMenu(LOCATIONS);
                                } else {
                                    openContextMenu(LOCATIONS);
                                }
                            }}
                            onInputClick={(value: string) => {
                                if (!!value) {
                                    closeContextMenu(LOCATIONS);
                                } else {
                                    openContextMenu(LOCATIONS);
                                }
                                this.context.headerItemClicked('Search Bar');
                            }}
                        />

                        {/* Locations box */}
                        <div
                            className={cls(styles.locationsDropdown, { [styles.locationsDropdownShowing]: isContextMenuOpen(LOCATIONS) })}
                            data-testid='HeaderLocationsDropdown'
                        >
                            <LocationsListing
                                onItemClick={(name: string) =>
                                    this.context.headerLocationItemClicked(this.props.locationsMenuTrackingSource || 'Other', name)
                                }
                            />
                        </div>
                    </div>
                )}
            </>
        );
    }

    /**
     * Render auth modals
     */
    private renderAuthModals() {
        return (
            <div>
                <AuthModal/>
            </div>
        );
    }

    private toggleContextMenu(target: HeaderContextMenus) {
        const { isContextMenuOpen, openContextMenu, closeContextMenu } = this.context;

        if (isContextMenuOpen(target)) {
            closeContextMenu(target);
        } else {
            openContextMenu(target);
        }
    }

    /**
     * Render the links that appear on the right side of the header on desktop
     */
    private renderRightLinks(userIsAuthed: boolean, userInfo: UserInfo | null) {
        let linksComponent;
        const subheaderLinks = this.getSubheaderLinksData();

        if (userInfo?.isWeddingVendor) {
            // Current user is vendor
            linksComponent = (
                <HeaderRightLinksVendor
                    userFirstName={userInfo.firstName}
                    unreadMessageCount={this.props.unreadMessagesCount}
                    unreadReviewsCount={this.props.unreadReviewsCount}
                    curMarket={userInfo.lastMarket || 'Northern California'}
                    subheaderLinks={subheaderLinks || []}
                />
            );
        } else if (userIsAuthed && !!userInfo) {
            // Current user is authenticated couple
            linksComponent = (
                <HeaderRightLinksCouple
                    userFirstName={userInfo.firstName}
                    unreadMessageCount={this.props.unreadMessagesCount}
                    unreadReviewsCount={this.props.unreadReviewsCount}
                    curMarket={userInfo.lastMarket}
                    subheaderLinks={subheaderLinks}
                />
            );
        } else {
            // Current user is unauthenticated
            linksComponent = <HeaderRightLinksUnauth curMarket='Northern California' subheaderLinks={subheaderLinks} />;
        }

        return linksComponent;
    }

    /**
     * Render the mweb hamburger menu contents
     */
    private renderMwebMenu(userIsAuthed: boolean, userInfo: UserInfo | null) {
        var menu;
        const subheaderLinks = this.getSubheaderLinksData();

        if (userInfo?.isWeddingVendor) {
            // Current user is vendor
            menu = (
                <MwebHeaderMenuVendor
                    unreadMessageCount={this.props.unreadMessagesCount}
                    unreadReviewsCount={this.props.unreadReviewsCount}
                    subheaderLinks={subheaderLinks}
                />
            );
        } else if (userIsAuthed && !!userInfo) {
            // Current user is authenticated couple
            menu = (
                <MwebHeaderMenuCouple
                    unreadMessageCount={this.props.unreadMessagesCount}
                    unreadReviewsCount={0}
                    subheaderLinks={subheaderLinks}
                />
            );
        } else {
            // Current user is unauthenticated
            menu = <MwebHeaderMenuUnauth subheaderLinks={subheaderLinks} />;
        }

        return menu;
    }

    private getSubheaderLinksData() {
        return (this.context?.headerData?.links || []).map(({ name, url, type, category, show_on_mobile }) => ({
            label: name,
            url: url.toLowerCase(),
            type: type,
            column: category,
            showOnMobile: show_on_mobile,
        }));
    }
}

export default Header;
